// LOCAL: epoch detail from checkpoint RPC (no suix_getEpochs).

import { formatAmount } from '@mysten/core';
import { LoadingIndicator, Text } from '@mysten/ui';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { EpochProgress } from './stats/EpochProgress';
import { EpochStats } from './stats/EpochStats';
import { ValidatorStatus } from './stats/ValidatorStatus';
import { PageLayout } from '~/components/Layout/PageLayout';
import { CheckpointsTable } from '~/components/checkpoints/CheckpointsTable';
import { useCpcEpochDetail } from '~/hooks/useCpcEpochDetail';
import { Banner } from '~/ui/Banner';
import { Stats } from '~/ui/Stats';

export default function CpcEpochDetail() {
	const { id } = useParams();
	const { data, isPending, isError } = useCpcEpochDetail(id);

	const epoch = data?.summary;
	const isCurrentEpoch = epoch?.isCurrent ?? false;

	const epochEndMs = useMemo(() => {
		if (!epoch) return 0;
		if (epoch.epochEndTimestamp) return epoch.epochEndTimestamp;
		if (isCurrentEpoch && data?.epochStartMs && data.epochDurationMs > 0) {
			return data.epochStartMs + data.epochDurationMs;
		}
		return 0;
	}, [data?.epochDurationMs, data?.epochStartMs, epoch, isCurrentEpoch]);

	const initialCursorPlusOne = epoch?.lastCheckpointId
		? (Number(epoch.lastCheckpointId) + 1).toString()
		: undefined;

	if (isPending) return <PageLayout content={<LoadingIndicator />} />;

	if (isError || !epoch)
		return (
			<PageLayout
				content={
					<Banner variant="error" fullWidth>
						{`There was an issue retrieving data for epoch ${id}.`}
					</Banner>
				}
			/>
		);

	const startMs =
		data?.epochStartMs ??
		(epoch.isCurrent ? Date.now() : 0);

	return (
		<PageLayout
			content={
				<div className="flex flex-col space-y-16">
					<div className="grid grid-flow-row gap-4 sm:gap-2 md:flex md:gap-6">
						<div className="flex min-w-[136px] max-w-[240px]">
							<EpochProgress
								epoch={epoch.epoch.toString()}
								inProgress={isCurrentEpoch}
								start={startMs}
								end={epochEndMs > 0 ? epochEndMs : undefined}
							/>
						</div>

						<EpochStats label="Overview">
							<Stats
								label="Transaction Blocks"
								tooltip="Transaction blocks in this epoch (from checkpoint scan)"
							>
								{formatAmount(epoch.epochTotalTransactions)}
							</Stats>
							<Stats label="First Checkpoint" tooltip="">
								{epoch.firstCheckpointId}
							</Stats>
							<Stats label="Last Checkpoint" tooltip="">
								{epoch.lastCheckpointId}
							</Stats>
							<Stats
								label="Storage Net Inflow"
								tooltip="Approximate from epoch rolling gas summary on last checkpoint"
							>
								{epoch.storageNetInflow != null
									? formatAmount(epoch.storageNetInflow)
									: '--'}
							</Stats>
						</EpochStats>

						{isCurrentEpoch ? <ValidatorStatus /> : null}
					</div>

					<div className="flex flex-col gap-4">
						<Text variant="heading4/semibold" color="steel-darker">
							Checkpoints
						</Text>
						<CheckpointsTable
							initialCursor={initialCursorPlusOne}
							maxCursor={epoch.firstCheckpointId}
							initialLimit={20}
						/>
					</div>
				</div>
			}
		/>
	);
}
